import { defineStore } from 'pinia'
import { ref } from 'vue'

export type ToastVariant = 'success' | 'error' | 'info' | 'warning'

export interface Toast {
  id: number
  message: string
  variant: ToastVariant
  timeout: number
}

export interface ConfirmRequest {
  title: string
  body?: string
  confirmLabel?: string
  cancelLabel?: string
  variant?: 'danger' | 'primary'
}

let nextToastId = 1

export const useUiStore = defineStore('ui', () => {
  const toasts = ref<Toast[]>([])
  const sidebarOpen = ref(false)

  const confirmRequest = ref<ConfirmRequest | null>(null)
  let confirmResolve: ((value: boolean) => void) | null = null

  function notify(message: string, variant: ToastVariant = 'info', timeout = 4500): void {
    const toast: Toast = { id: nextToastId++, message, variant, timeout }
    toasts.value.push(toast)

    if (timeout > 0) {
      setTimeout(() => dismiss(toast.id), timeout)
    }
  }

  const success = (message: string) => notify(message, 'success')
  const error = (message: string) => notify(message, 'error', 7000)
  const info = (message: string) => notify(message, 'info')
  const warning = (message: string) => notify(message, 'warning', 6000)

  function dismiss(id: number): void {
    toasts.value = toasts.value.filter((toast) => toast.id !== id)
  }

  /**
   * Promise-based confirmation, so callers read as a linear flow:
   *   if (!(await confirm({ title }))) return
   */
  function confirm(request: ConfirmRequest): Promise<boolean> {
    confirmRequest.value = request
    return new Promise((resolve) => {
      confirmResolve = resolve
    })
  }

  function resolveConfirm(value: boolean): void {
    confirmRequest.value = null
    confirmResolve?.(value)
    confirmResolve = null
  }

  function toggleSidebar(open?: boolean): void {
    sidebarOpen.value = open ?? !sidebarOpen.value
  }

  return {
    toasts,
    sidebarOpen,
    confirmRequest,
    notify,
    success,
    error,
    info,
    warning,
    dismiss,
    confirm,
    resolveConfirm,
    toggleSidebar,
  }
})
